import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText,
  Download,
  FileSpreadsheet,
  Calendar,
  Loader2,
  Printer,
  X,
  AlertCircle,
  CheckCircle2,
  TrendingDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";

export default function CustomerStatementModal({
  open,
  onOpenChange,
  customerId: propCustomerId,
  customerName: propCustomerName,
  customer,
}) {
  const customerId = propCustomerId || customer?.id;
  const customerName =
    propCustomerName ||
    customer?.display_name ||
    customer?.company_name ||
    "Müşteri";

  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const fetchStatement = useCallback(async (start = "", end = "") => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;

      const res = await api.get(`/customers/${customerId}/statement`, { params });
      setStatement(res.data);
    } catch (err) {
      const errorMsg = formatApiError(err, "Cari hesap ekstresi yüklenemedi.");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) {
      setStartDate("");
      setEndDate("");
      setError(null);
      fetchStatement("", "");
    } else if (!open) {
      setStatement(null);
      setError(null);
    }
  }, [open, customerId, fetchStatement]);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchStatement(startDate, endDate);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    fetchStatement("", "");
  };

  const handleDownload = async (type) => {
    const isPdf = type === "pdf";
    const setter = isPdf ? setPdfLoading : setExcelLoading;
    setter(true);

    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const endpoint = `/customers/${customerId}/statement/${isPdf ? "pdf" : "excel"}`;
      const response = await api.get(endpoint, {
        params,
        responseType: "blob",
      });

      const mimeType = isPdf
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([response.data], { type: mimeType });

      let filename = `ekstre_${isPdf ? "raporu.pdf" : "raporu.xlsx"}`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isPdf ? "PDF ekstresi indirildi." : "Excel ekstresi indirildi.");
    } catch (err) {
      toast.error(formatApiError(err, isPdf ? "PDF indirilemedi." : "Excel indirilemedi."));
    } finally {
      setter(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const summary = statement?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#DCE2E8] flex items-center justify-between bg-slate-50/50">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="text-[#FF353A]" size={20} />
              <DialogTitle className="text-lg font-bold text-[#171A1F]">
                Cari Hesap Ekstresi
              </DialogTitle>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {statement?.customer?.display_name || customerName} &bull; {statement?.customer?.customer_number || ""}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload("excel")}
              disabled={excelLoading || loading || !statement}
              className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8"
              data-testid="btn-export-excel"
            >
              {excelLoading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <FileSpreadsheet size={13} className="mr-1.5" />}
              Excel İndir
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload("pdf")}
              disabled={pdfLoading || loading || !statement}
              className="text-xs border-red-300 text-red-700 hover:bg-red-50 h-8"
              data-testid="btn-export-pdf"
            >
              {pdfLoading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Download size={13} className="mr-1.5" />}
              PDF İndir
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={loading || !statement}
              className="text-xs text-slate-700 h-8"
            >
              <Printer size={13} className="mr-1.5" />
              Yazdır
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <form onSubmit={handleFilter} className="px-6 py-3 border-b border-[#DCE2E8] bg-slate-50 flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5 text-slate-600 font-medium">
            <Calendar size={14} className="text-slate-400" />
            <span>Tarih Aralığı:</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 text-xs border-[#DCE2E8] w-36 bg-white"
              data-testid="statement-start-date"
            />
            <span className="text-slate-400">&mdash;</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 text-xs border-[#DCE2E8] w-36 bg-white"
              data-testid="statement-end-date"
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={loading || !customerId}
            className="h-8 bg-[#171A1F] hover:bg-slate-800 text-white text-xs px-3"
            data-testid="btn-statement-filter"
          >
            Filtrele
          </Button>
          {(startDate || endDate) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFilter}
              className="h-8 text-xs text-slate-500 hover:text-slate-800 px-2"
            >
              <X size={13} className="mr-1" />
              Filtreyi Temizle
            </Button>
          )}
        </form>

        {/* Statement Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-500">
              <Loader2 size={24} className="animate-spin text-[#FF353A] mb-2" />
              <p className="text-xs">Ekstre hareketleri hesaplanıyor...</p>
            </div>
          ) : error ? (
            <div className="py-16 px-6 flex flex-col items-center justify-center text-center" data-testid="statement-error-state">
              <div className="p-3 bg-red-50 text-red-600 rounded-full mb-3">
                <AlertCircle size={28} />
              </div>
              <p className="text-sm font-semibold text-slate-800">Cari Hesap Ekstresi Yüklenemedi</p>
              <p className="text-xs text-slate-500 mt-1 max-w-md">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchStatement(startDate, endDate)}
                className="mt-4 text-xs border-slate-300 hover:bg-slate-50"
                data-testid="btn-retry-statement"
              >
                Yeniden Dene
              </Button>
            </div>
          ) : !customerId ? (
            <div className="py-16 text-center text-slate-400 text-xs">
              Müşteri bilgileri bekleniyor...
            </div>
          ) : !statement ? (
            <div className="py-16 text-center text-slate-400 text-xs">
              Ekstre bilgisi bulunamadı.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Transactions Table or Empty Box */}
              {!statement.transactions || statement.transactions.length === 0 ? (
                <div
                  className="p-8 text-center text-slate-500 text-xs border border-dashed border-[#DCE2E8] rounded-lg bg-slate-50/50"
                  data-testid="statement-empty-state"
                >
                  <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                  <p className="font-medium text-slate-700">Finansal Hareket Bulunamadı</p>
                  <p className="text-slate-500 mt-0.5">
                    {startDate || endDate
                      ? "Seçilen tarih aralığında bu cari hesaba ait herhangi bir işlem (iş, tahsilat, iskonto) bulunamadı."
                      : "Bu cari hesaba ait henüz herhangi bir işlem (iş, tahsilat, iskonto) kaydedilmemiştir."}
                  </p>
                </div>
              ) : (
                <div className="rounded border border-[#DCE2E8] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100/80 hover:bg-slate-100/80">
                        <TableHead className="w-24 text-xs font-semibold text-slate-700">Tarih</TableHead>
                        <TableHead className="w-36 text-xs font-semibold text-slate-700">İşlem Türü</TableHead>
                        <TableHead className="w-32 text-xs font-semibold text-slate-700">Belge / Ref</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-700">Açıklama</TableHead>
                        <TableHead className="text-right w-32 text-xs font-semibold text-slate-700">Borç</TableHead>
                        <TableHead className="text-right w-32 text-xs font-semibold text-slate-700">Alacak</TableHead>
                        <TableHead className="text-right w-32 text-xs font-semibold text-slate-700">Yürüyen Bakiye</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statement.transactions.map((trx, idx) => {
                        const isOpening = trx.transaction_type === "opening_balance";
                        const debitVal = parseFloat(trx.debit || 0);
                        const creditVal = parseFloat(trx.credit || 0);
                        const balVal = parseFloat(trx.running_balance || 0);

                        return (
                          <TableRow
                            key={trx.id || idx}
                            className={isOpening ? "bg-amber-50/50 font-semibold" : "hover:bg-slate-50"}
                            data-testid={`statement-row-${trx.id || idx}`}
                          >
                            <TableCell className="text-xs text-slate-600 font-mono">
                              {formatDate(trx.date)}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-slate-800">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                trx.transaction_type === "job"
                                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                                  : trx.transaction_type === "customer_payment"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : (trx.transaction_type === "customer_discount" || trx.transaction_type === "job_discount")
                                  ? "bg-purple-50 text-purple-700 border border-purple-200"
                                  : "bg-slate-100 text-slate-700"
                              }`}>
                                {trx.transaction_type_label}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-slate-700 font-mono">
                              {trx.reference || "-"}
                            </TableCell>
                            <TableCell className="text-xs text-slate-600 whitespace-pre-line">
                              {trx.description || "-"}
                            </TableCell>
                            <TableCell className="text-right text-xs font-mono text-slate-800">
                              {debitVal > 0 ? formatCurrency(debitVal) : "-"}
                            </TableCell>
                            <TableCell className="text-right text-xs font-mono text-emerald-700 font-medium">
                              {creditVal > 0 ? formatCurrency(creditVal) : "-"}
                            </TableCell>
                            <TableCell className="text-right text-xs font-mono font-bold">
                              <span className={balVal > 0 ? "text-[#FF353A]" : balVal < 0 ? "text-emerald-700" : "text-slate-700"}>
                                {formatCurrency(balVal)}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* MANDATORY SUMMARY SECTION */}
              {summary && (
                <div
                  className="rounded-lg border-2 border-slate-300 bg-slate-50 p-5 shadow-sm space-y-4 max-w-lg ml-auto"
                  data-testid="statement-summary-box"
                >
                  <div className="border-b border-slate-200 pb-2">
                    <h3 className="text-sm font-bold text-[#171A1F] uppercase tracking-wider">
                      CARİ HESAP ÖZETİ
                    </h3>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between items-center text-slate-600">
                      <span>Toplam İş Bedeli:</span>
                      <span className="font-mono font-semibold text-slate-800" data-testid="statement-sum-jobs">
                        {formatCurrency(summary.total_jobs_amount || 0)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-slate-600">
                      <span>Toplam Tahsilat:</span>
                      <span className="font-mono font-semibold text-emerald-700" data-testid="statement-sum-collections">
                        {formatCurrency(summary.total_collections_amount || 0)}
                      </span>
                    </div>

                    {parseFloat(summary.total_discounts_amount || 0) > 0 && (
                      <div className="flex justify-between items-center text-slate-600">
                        <span>Uygulanan İskonto:</span>
                        <span className="font-mono font-semibold text-purple-700" data-testid="statement-sum-discounts">
                          {formatCurrency(summary.total_discounts_amount || 0)}
                        </span>
                      </div>
                    )}

                    <div
                      className={`border-t border-slate-200 pt-3 flex justify-between items-center text-sm font-bold ${
                        parseFloat(summary.closing_balance || 0) > 0
                          ? "text-[#FF353A]"
                          : parseFloat(summary.closing_balance || 0) < 0
                          ? "text-amber-700"
                          : "text-slate-800"
                      }`}
                    >
                      <span data-testid="statement-closing-label">
                        {parseFloat(summary.closing_balance || 0) < 0
                          ? "MÜŞTERİ AVANS BAKİYESİ:"
                          : "KALAN BAKİYE:"}
                      </span>
                      <span className="font-mono text-base font-extrabold" data-testid="statement-sum-closing">
                        {formatCurrency(Math.abs(parseFloat(summary.closing_balance || 0)))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
